/**
 * <client-sort>
 *
 * Applies the sort options Shopify cannot apply itself, so that collection and
 * search pages can offer one identical list of options:
 *  - Season lives in the `custom.seizoen` metafield, and `sort_by` cannot order
 *    on a metafield anywhere.
 *  - Date is missing from the search page's sort options, so Shopify silently
 *    ignores `sort_by=created-descending` there. On a collection it works
 *    natively and this component stays out of the way.
 *
 * Which values it owns is decided in Liquid and passed in via `data-sorts`.
 *
 * It also moves sold out products below the available ones when
 * `data-sold-out-last` is set. Shopify has no sort order that does this, and on
 * search it is handled server side by `options[unavailable_products]=last`. On a
 * collection there is no equivalent, and since most of the range is second hand
 * a collection sorted alphabetically or by best seller can open on a page of
 * nothing but sold out shirts.
 *
 * The grid is reordered in place, on the data attributes written by
 * `snippets/product-sort-data.liquid`. That orders the products that are loaded,
 * so with pagination it does not reorder across pages.
 *
 * The selected option stays in the `sort_by` query parameter, exactly like the
 * native options. Shopify ignores the unknown value and returns its default
 * order, after which this component reorders the result.
 */
if (!customElements.get('client-sort')) {
  class ClientSort extends HTMLElement {
    /**
     * Reads the sort values this component is responsible for.
     * @returns {string[]}
     */
    get sortValues() {
      return (this.dataset.sorts || '').split(',').filter(Boolean);
    }

    /**
     * Returns the active sort value when this component owns it, else null.
     * @returns {?string}
     */
    getActiveSort() {
      const value = new URLSearchParams(window.location.search).get('sort_by');
      return this.sortValues.includes(value) ? value : null;
    }

    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.applyHandler = this.apply.bind(this);
      document.addEventListener('on:facet-filters:updated', this.applyHandler);
      window.addEventListener('popstate', this.applyHandler);

      this.apply();
    }

    disconnectedCallback() {
      document.removeEventListener('on:facet-filters:updated', this.applyHandler);
      window.removeEventListener('popstate', this.applyHandler);
      this.stopObserving();
    }

    /**
     * Whether sold out products should be moved below the available ones.
     * @returns {boolean}
     */
    get soldOutLast() {
      return this.dataset.soldOutLast === 'true';
    }

    /**
     * Syncs the sort controls with the url and reorders the grid when needed.
     */
    apply() {
      const activeSort = this.getActiveSort();

      if (!activeSort && !this.soldOutLast) {
        this.stopObserving();
        return;
      }

      if (activeSort) this.syncControls(activeSort);

      this.reorderGrid(activeSort);
    }

    /**
     * Reflects the active sort in the sort controls.
     *
     * Liquid cannot know the value is in use, because Shopify drops it before
     * rendering, so the controls come back with no option selected.
     * @param {string} activeSort - Active sort value.
     */
    syncControls(activeSort) {
      // Mobile filter drawer: check the matching radio button.
      const radio = document.querySelector(`.js-drawer-sort-by[value="${activeSort}"]`);
      if (radio && !radio.checked) radio.checked = true;

      // Desktop dropdown: select the matching option.
      const select = document.getElementById('products-sort-by');
      const option = select && select.querySelector(`.js-option[data-value="${activeSort}"]`);
      if (!option || option.getAttribute('aria-selected') === 'true') return;

      const currentOption = select.querySelector('[aria-selected="true"]');
      if (currentOption) currentOption.setAttribute('aria-selected', 'false');
      option.setAttribute('aria-selected', 'true');

      const listbox = select.querySelector('.custom-select__listbox');
      if (listbox) listbox.setAttribute('aria-activedescendant', option.id);

      const button = select.querySelector('.custom-select__btn');
      if (button && button.firstElementChild) {
        button.firstElementChild.textContent = option.firstElementChild.textContent;
      }

      select.selectedOption = option;

      // The button width is based on the selected option, so it needs recalculating.
      if (typeof select.setButtonWidth === 'function') select.setButtonWidth();
    }

    /**
     * The data attribute and direction a sort value maps to.
     * @param {string} sortValue - Active sort value.
     * @returns {?{attribute: string, ascending: boolean}}
     */
    static getSortKey(sortValue) {
      switch (sortValue) {
        // A product can cover several seasons, so use the earliest when sorting
        // old to new and the latest when sorting new to old.
        case 'season-ascending':
          return { attribute: 'seasonMin', ascending: true };
        case 'season-descending':
          return { attribute: 'seasonMax', ascending: false };
        case 'created-ascending':
          return { attribute: 'created', ascending: true };
        case 'created-descending':
          return { attribute: 'created', ascending: false };
        // Alphabetical is a collection-only sort in Shopify, so on search this
        // component has to apply it.
        case 'title-ascending':
          return { attribute: 'title', ascending: true, text: true };
        case 'title-descending':
          return { attribute: 'title', ascending: false, text: true };
        default:
          return null;
      }
    }

    /**
     * Reorders the product grid: first by the active sort when this component
     * owns it, then moving sold out products below the available ones.
     * @param {?string} sortValue - Active sort value, or null.
     */
    reorderGrid(sortValue) {
      const key = sortValue ? ClientSort.getSortKey(sortValue) : null;
      const grid = document.querySelector('#filter-results .grid');
      if (!grid || (!key && !this.soldOutLast)) return;

      this.observeGrid(grid);

      const children = Array.from(grid.children);
      const items = children.filter((el) => el.hasAttribute('data-season-min'));
      if (items.length < 2) return;

      let sorted = items.slice();

      if (key) {
        sorted.sort((a, b) => {
          if (key.text) {
            const aText = a.dataset[key.attribute] || '';
            const bText = b.dataset[key.attribute] || '';

            // Products without a value always go last, in both directions.
            if (!aText && !bText) return 0;
            if (!aText) return 1;
            if (!bText) return -1;

            // `numeric` keeps the season in a title in order, so that 1998 sorts
            // before 2011 rather than between 1 and 2.
            const order = aText.localeCompare(bText, undefined, { numeric: true, sensitivity: 'base' });
            return key.ascending ? order : -order;
          }

          const aValue = parseInt(a.dataset[key.attribute], 10);
          const bValue = parseInt(b.dataset[key.attribute], 10);
          const aKnown = Number.isFinite(aValue);
          const bKnown = Number.isFinite(bValue);

          // Products without a value always go last, in both directions.
          if (!aKnown && !bKnown) return 0;
          if (!aKnown) return 1;
          if (!bKnown) return -1;

          return key.ascending ? aValue - bValue : bValue - aValue;
        });
      }

      if (this.soldOutLast) {
        // A stable partition, so the order chosen above survives within each half.
        const available = sorted.filter((el) => el.dataset.available !== 'false');
        const soldOut = sorted.filter((el) => el.dataset.available === 'false');
        sorted = available.concat(soldOut);
      }

      if (sorted.every((el, index) => el === items[index])) return;

      // Reuse the positions the products already occupied, so that promotion
      // blocks keep the position they are configured to appear in.
      const reordered = children.slice();
      let slot = 0;

      children.forEach((el, index) => {
        if (el.hasAttribute('data-season-min')) {
          reordered[index] = sorted[slot];
          slot += 1;
        }
      });

      this.stopObserving();
      grid.replaceChildren(...reordered);
      this.observeGrid(grid);

      // Keep the pagination in sync, it appends new results after the last one.
      const pagination = grid.closest('custom-pagination');
      if (pagination && pagination.results) {
        pagination.results = pagination.querySelectorAll('.js-pagination-result');
      }
    }

    /**
     * Watches the grid so that results added by the pagination are sorted too.
     * @param {Element} grid - Product grid element.
     */
    observeGrid(grid) {
      if (this.observedGrid === grid && this.gridObserver) return;

      this.stopObserving();
      this.gridObserver = new MutationObserver(() => {
        this.apply();
      });
      this.gridObserver.observe(grid, { childList: true });
      this.observedGrid = grid;
    }

    /**
     * Stops watching the grid.
     */
    stopObserving() {
      if (!this.gridObserver) return;
      this.gridObserver.disconnect();
      this.gridObserver = null;
      this.observedGrid = null;
    }
  }

  customElements.define('client-sort', ClientSort);
}
